import React, { useGlobal } from "reactn"
import Wrapper from "../components/Wrapper"
import InterestForm from "../components/InterestForm"
import Sidebar from "react-sidebar"
import styled from "styled-components"
import autoscroll from "../utils/autoscroll"

interface SidebarContentProps {
  close: Function
}

const SidebarContentContainer = styled.div`
  width: 50vw;
  min-width: 500px;
  @media(max-width: 639px) {
    min-width: auto;
    width: 100%;
  }
`

interface ScrollToProps {
  to: string
  children: React.ReactNode
  className?: string
}

const SidebarContent: React.FC<SidebarContentProps> = ({ close }) => {
  const ScrollTo: React.FC<ScrollToProps> = ({ to, className, children }) => {
    const scroll = (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      close()
      autoscroll({
        container: document.getElementById("page-content"),
        destination: document.getElementById(to),
        duration: 400
      })
    }

    return (
      <a
        href={`/#${to}`}
        onClick={scroll}
        className={className}
      >
        {children}
      </a>
    )
  }

  return (
    <SidebarContentContainer className="py-12 sm:pt-20">
      <Wrapper
        size="s"
        className="mx-auto px-10 sm:px-6"
      >
        <button
          className="absolute top-20 right-20 z-10 text-3xl -mt-2 leading-none sm:hidden"
          aria-label="Lukk menyen"
          onClick={() => close()}
        >
          &times;
        </button>

        <section className="flex">
          <nav
            role="navigation"
            aria-label="Prosjektets meny"
            className="flex flex-1 flex-col items-start text-lg"
          >
            <h3 className="text-xl font-semibold mb-3">
              Eugenies Gate 5
            </h3>
            <ScrollTo
              className="text-accent hover:text-white leading-normal"
              to="om-prosjektet"
            >
              01. Om prosjektet
            </ScrollTo>
            <ScrollTo
              className="text-accent hover:text-white leading-normal"
              to="standarder-og-kvaliteter"
            >
              02. Kvalitet og standard
            </ScrollTo>
            <ScrollTo
              className="text-accent hover:text-white leading-normal"
              to="egenskaper"
            >
              03. Egenskaper
            </ScrollTo>
            <ScrollTo
              className="text-accent hover:text-white leading-normal"
              to="leilighetene"
            >
              04. Leilighetene
            </ScrollTo>
            <ScrollTo
              className="text-accent hover:text-white leading-normal"
              to="omradet"
            >
              05. Området
            </ScrollTo>
          </nav>

          <nav
            role="navigation"
            aria-label="Bonum meny"
            className="flex flex-1 flex-col items-start text-lg"
          >
            <h3 className="text-xl font-semibold mb-3">
              Bonum
            </h3>
            <a
              className="text-accent hover:text-white leading-normal"
              href="https://bonum.no"
              target="_blank"
              rel="noopener"
            >
              01. Om selskapet
            </a>
            <a
              className="text-accent hover:text-white leading-normal"
              href="https://bonum.no/prosjekter"
              target="_blank"
              rel="noopener"
            >
              02. Flere prosjekter
            </a>
            <a
              className="text-accent hover:text-white leading-normal"
              href="https://bonum.no/ansatte"
              target="_blank"
              rel="noopener"
            >
              03. Våre ansatte
            </a>
            <a
              className="text-accent hover:text-white leading-normal"
              href="https://bonum.no/media"
              target="_blank"
              rel="noopener"
            >
              04. Bonum i media
            </a>
          </nav>
        </section>

        <section className="mt-12">
          <h3 className="text-xl font-semibold mb-3">
            Interessert i Eugenies Gate 5?
          </h3>

          <p className="mb-8">
            Fører du deg opp som interessent for Eugenies Gate 5 vil megler kontakte deg så snart som mulig for mer informasjon.
          </p>

          <InterestForm />
        </section>
      </Wrapper>
    </SidebarContentContainer>
  )
}

const OpenSidebarButtonText = styled.div`
  &:after {
    left: 100%;
    top: 50%;
    border: solid transparent;
    content: "";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    border-color: rgba(136, 183, 213, 0);
    border-left-color: #000;
    border-width: 7px;
    margin-top: -7px;
  }
`

interface PageSidebarProps {
  children: JSX.Element[] | JSX.Element
}

const PageSidebar: React.FC<PageSidebarProps> = ({ children }) => {
  const [ sidebarIsOpen, setSidebarIsOpen ] = useGlobal<any>("sidebarIsOpen")

  return (
    <Sidebar
      contentId="page-content"
      open={sidebarIsOpen}
      onSetOpen={setSidebarIsOpen}
      styles={{
        sidebar: {
          backgroundColor: "#0e101c"
        }
      }}
      sidebar={(
        <SidebarContent close={() => setSidebarIsOpen(false)} />
      )}
    >
      <div
        className="fixed top-20 right-20 z-10 flex items-center"
        onClick={() => setSidebarIsOpen(!sidebarIsOpen)}
      >
        <OpenSidebarButtonText className="text-white uppercase text-xs bg-black px-3 py-1 rounded-sm mr-4 relative shadow font-semibold">
          Meld interesse
        </OpenSidebarButtonText>
        <button
          aria-label="Åpne menyen"
          className="border-2 border-white border-solid w-10 h-10 rounded-full hover:bg-black hover:border-black"
        >
          <hr className="bg-white w-1/2 h-px mx-auto" />
          <hr className="bg-white w-1/2 h-px mx-auto my-1" />
          <hr className="bg-white w-1/2 h-px mx-auto" />
        </button>
      </div>

      {children}
    </Sidebar>
  )
}

export default PageSidebar
